import { prisma } from "client";
import {
  ghostEmailCandidates,
  missingRequiredWhiteLabel,
  normalizeUserEmail,
  WHITE_LABEL_REQUIRED_MESSAGE,
} from "./scopedUser.pure";

export {
  ghostEmailCandidates,
  missingRequiredWhiteLabel,
  normalizeUserEmail,
  WHITE_LABEL_REQUIRED_MESSAGE,
} from "./scopedUser.pure";

const scopedUserSelect = {
  id: true,
  email: true,
  name: true,
  accessibleWhiteLabelId: true,
} as const;

export type ScopedUserFields = {
  id: string;
  email: string | null;
  name: string | null;
  accessibleWhiteLabelId: string | null;
};

export type FindOrCreateScopedUserInput = {
  email: string;
  whiteLabelId?: string | null;
  allowUnscoped?: boolean;
  name?: string | null;
  phoneNumber?: string | null;
  image?: string | null;
  participantImportId?: string | null;
  password?: string | null;
  active?: boolean;
};

function createData(
  input: FindOrCreateScopedUserInput,
  email: string
): Parameters<typeof prisma.user.create>[0]["data"] {
  return {
    email,
    name: input.name ?? "",
    ...(input.phoneNumber ? { phoneNumber: input.phoneNumber } : {}),
    ...(input.image ? { image: input.image } : {}),
    ...(input.participantImportId
      ? { participantImportId: input.participantImportId }
      : {}),
    ...(input.password ? { password: input.password } : {}),
    ...(input.active !== undefined ? { active: input.active } : {}),
    ...(input.whiteLabelId
      ? { accessibleWhiteLabelId: input.whiteLabelId }
      : {}),
  };
}

async function findScopedUser(
  email: string,
  whiteLabelId: string
): Promise<ScopedUserFields | null> {
  return prisma.user.findUnique({
    where: {
      email_accessibleWhiteLabelId: {
        email,
        accessibleWhiteLabelId: whiteLabelId,
      },
    },
    select: scopedUserSelect,
  });
}

/** Attach WL to a no-WL row with the same email. No-op if the scoped user already exists. */
export async function adoptUnscopedUser(
  email: string,
  whiteLabelId: string
): Promise<ScopedUserFields | null> {
  const normalized = normalizeUserEmail(email);
  const scoped = await findScopedUser(normalized, whiteLabelId);
  if (scoped) return scoped;

  const ghost = await prisma.user.findFirst({
    where: {
      accessibleWhiteLabelId: null,
      email: { in: ghostEmailCandidates(email) },
      NOT: {
        OR: [
          { roleV2: { is: { name: "SUPER_ADMIN" } } },
          {
            userRoleAssignments: {
              some: { role: { name: "SUPER_ADMIN" } },
            },
          },
          { roles: { some: { role: { name: "SUPER_ADMIN" } } } },
        ],
      },
    },
    select: scopedUserSelect,
  });
  if (!ghost) return null;

  try {
    return await prisma.user.update({
      where: { id: ghost.id },
      data: { email: normalized, accessibleWhiteLabelId: whiteLabelId },
      select: scopedUserSelect,
    });
  } catch {
    return findScopedUser(normalized, whiteLabelId);
  }
}

export async function findOrCreateScopedUser(
  input: FindOrCreateScopedUserInput
): Promise<ScopedUserFields> {
  const email = normalizeUserEmail(input.email);
  if (!email) throw new Error("Email is required");
  if (missingRequiredWhiteLabel(input.whiteLabelId, input.allowUnscoped)) {
    throw new Error(WHITE_LABEL_REQUIRED_MESSAGE);
  }

  if (input.whiteLabelId) {
    const existing = await findScopedUser(email, input.whiteLabelId);
    if (existing) return existing;

    const adopted = await adoptUnscopedUser(input.email, input.whiteLabelId);
    if (adopted) {
      if (!input.password && !input.phoneNumber && !input.name) return adopted;
      return prisma.user.update({
        where: { id: adopted.id },
        data: {
          ...(input.password ? { password: input.password } : {}),
          ...(input.active !== undefined ? { active: input.active } : {}),
          ...(input.phoneNumber ? { phoneNumber: input.phoneNumber } : {}),
          ...(input.name ? { name: input.name } : {}),
        },
        select: scopedUserSelect,
      });
    }

    try {
      return await prisma.user.create({
        data: createData(input, email),
        select: scopedUserSelect,
      });
    } catch (error) {
      const raced = await findScopedUser(email, input.whiteLabelId);
      if (raced) return raced;
      throw error;
    }
  }

  const unscoped = await prisma.user.findFirst({
    where: {
      email: { in: ghostEmailCandidates(input.email) },
      accessibleWhiteLabelId: null,
    },
    select: scopedUserSelect,
  });
  if (unscoped) return unscoped;

  return prisma.user.create({
    data: createData({ ...input, whiteLabelId: null }, email),
    select: scopedUserSelect,
  });
}
